// src/lib/api/client.ts
import { Context } from '@/util-components/root/context';
import * as Sentry from "@sentry/react";
import { QueryClient } from '@tanstack/react-query';
import { useMemo, useContext } from 'react';
import { getErrorMessage } from '../utils/handle-error';
import { getBrowserEnvironment } from '../utils/environment';
import { getCurrentTraceId, sanitizeErrorResponseBody } from '../monitoring/sentry';

// QueryClient-Instanzen pro Department kapseln, damit beim Dept-Wechsel
// ein separater Cache verwendet wird, ohne die Query-Keys anzupassen.
const clientsByDepartment = new Map<string, QueryClient>();

function createClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  });
}

export function getQueryClientForDepartment(department?: string): QueryClient {
  const key = (department && department.trim()) || 'default';
  let client = clientsByDepartment.get(key);
  if (!client) {
    client = createClient();
    clientsByDepartment.set(key, client);
  }
  return client;
}

// Base API URL
export const API_URL = process.env.REACT_APP_API || 'http://localhost:3001/v1.0';

// Error type definition
export interface ApiError {
  status: number;
  message: string;
  data?: any;
}

export interface ApiRequestOptions {
  skipGlobalErrorHandler?: boolean;
  throwOnError?: boolean;
  silent?: boolean;
  responseType?: 'json' | 'blob';
}

type SerializableRecord = Record<string, unknown>;

function sanitizeTelemetryUrl(value: string): string {
  try {
    const parsed = new URL(value);
    
    // Sanitize MongoDB ObjectIDs in the path
    const pathParts = parsed.pathname.split('/');
    const sanitizedPath = pathParts.map(part => {
      // Simple regex for 24-character hex string (MongoDB ObjectID)
      if (/^[0-9a-fA-F]{24}$/.test(part)) {
        return ':id';
      }
      return part;
    }).join('/');

    return `${parsed.origin}${sanitizedPath}`;
  } catch {
    return value.split("?")[0]?.split("#")[0] ?? value;
  }
}

function summarizeRequestPayload(body: unknown): unknown {
  if (body === null || body === undefined) {
    return undefined;
  }

  if (body instanceof FormData) {
    const keys = Array.from(body.keys()).slice(0, 20);
    return {
      kind: "FormData",
      fieldCount: keys.length,
      fields: keys,
    };
  }

  if (Array.isArray(body)) {
    return {
      kind: "array",
      itemCount: body.length,
    };
  }

  if (typeof body === "string") {
    return {
      kind: "string",
      length: body.length,
    };
  }

  if (typeof body === "object") {
    return {
      kind: "object",
      keys: Object.keys(body as SerializableRecord).slice(0, 20),
    };
  }

  return {
    kind: typeof body,
  };
}

function sanitizeRequestHeaders(headers: Record<string, string>): Record<string, string> {
  return Object.entries(headers).reduce<Record<string, string>>((acc, [key, value]) => {
    const normalizedKey = key.toLowerCase();

    if (normalizedKey === "authorization") {
      acc[key] = "[redacted]";
      return acc;
    }

    if (normalizedKey === "content-type" || normalizedKey === "x-correlation-id" || normalizedKey === "x-department") {
      acc[key] = value;
    }

    return acc;
  }, {});
}

// Custom hook that provides an API client with context values
export function useApiClient() {
  const { setError, getAccessToken, currentDepartment, availableDepartments} = useContext(Context);
  const browserEnvironment = useMemo(() => getBrowserEnvironment(), []);

  const apiRequest = async <T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
    body?: any,
    customHeaders?: Record<string, string>,
    options: ApiRequestOptions = {}
  ): Promise<T> => {
    const url = `${API_URL}/${endpoint.startsWith('/') ? endpoint.substring(1) : endpoint}`;
    const correlationId = crypto.randomUUID();

    const headers: Record<string, string> = {
      ...customHeaders,
      'X-Correlation-Id': correlationId,
    };

    // Only set Content-Type for non-FormData requests
    if (!(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const deptValue =
          currentDepartment ||
          (typeof window !== 'undefined' ? window.localStorage.getItem('currentDepartment') || undefined : undefined) ||
          (Array.isArray(availableDepartments) && availableDepartments.length > 0 ? availableDepartments[0] : undefined);

    if(deptValue) {
        headers['X-Department'] = deptValue;
    }

    const config: RequestInit = {
      method,
      headers,
      // Only stringify non-FormData bodies
      body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
    };

    // Capture stack trace for debug context
    const stackTrace = new Error().stack;
    const sentryTraceId = getCurrentTraceId();
    const sanitizedHeaders = sanitizeRequestHeaders(headers);

    try {
      const response = await fetch(url, config);
      const responseTraceId = response.headers.get('x-trace-id') ?? sentryTraceId;
      const responseCorrelationId = response.headers.get('x-correlation-id') ?? correlationId;

      if (!response.ok) {
        // Clone response to read body for debug context
        const responseClone = response.clone();
        let responseBody;
        try {
          responseBody = await responseClone.json();
        } catch {
          try {
            responseBody = await responseClone.text();
          } catch {
            responseBody = 'Could not parse response body';
          }
        }

        const error = await getErrorMessage(response);

        // Attach debug context
        error.debugContext = {
          request: {
            method,
            url: sanitizeTelemetryUrl(url),
            headers: sanitizedHeaders,
            body: summarizeRequestPayload(body),
          },
          response: {
            status: response.status,
            statusText: response.statusText,
            body: sanitizeErrorResponseBody(responseBody),
            headers: {
              'x-correlation-id': responseCorrelationId,
              'x-trace-id': responseTraceId ?? '',
            },
          },
          environment: browserEnvironment,
          stackTrace,
          timestamp: new Date().toISOString(),
        };
        error.traceId = error.traceId ?? responseTraceId;
        error.correlationId = error.correlationId ?? responseCorrelationId;

        if (setError && !options.skipGlobalErrorHandler) {
          setError(error);
        }


        if (!options.silent) {
          console.error(
            JSON.stringify({
              type: 'api-response-error',
              method,
              url,
              status: response.status,
              message: error.message,
              correlationId: responseCorrelationId,
              traceId: responseTraceId,
            }),
          );
        }

        if (options.throwOnError) {
          throw error;
        }

        return {} as T;
      }

      // Handle empty responses
      if (response.status === 204) {
        console.log(JSON.stringify({ type: 'api-response', method, url, status: response.status, data: null }));
        return {} as T;
      }

      try {
        if (options.responseType === 'blob') {
          const blob = await response.blob();
          return blob as unknown as T;
        }
        
        const json = await response.json();
        return json as T;
      } catch (error) {
        console.error(JSON.stringify({ type: 'api-response-parse-error', method, url }));
        return {} as T;
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'statusCode' in error) {
        throw error;
      }

      const message = (error as any)?.message ?? 'Unknown error';
      Sentry.withScope((scope) => {
        scope.setTag('correlation_id', correlationId);
        if (sentryTraceId) {
          scope.setTag('trace_id', sentryTraceId);
        }
        scope.setContext('api_request', {
          method,
          url: sanitizeTelemetryUrl(url),
          headers: sanitizedHeaders,
          environment: browserEnvironment,
        });
        Sentry.captureException(error);
      });
      console.error(JSON.stringify({ type: 'api-error', method, url, message, correlationId, traceId: sentryTraceId }));
      throw error;
    }
  };

  return { apiRequest };
}
